/** The six-state Verdict, in words, glyphs and the passing set - one home (F07, REP-02).
 *
 * Reports and the home screen's per-filing standing both read these. They were Reports' own
 * constants until the home screen needed the same six sentences; a second copy of a verdict's
 * wording is how one screen comes to say "passed" for a state the other calls "not ready".
 * The state itself is the server's (`validation.Verdict`); this file only says how each reads.
 */

import { CircleDashed, ListChecks, Prohibit, SealCheck, Stamp, WarningCircle } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";

/** The server's six states, in ladder order. A value outside this list is not one of them. */
export const VERDICT_STATES = [
  "not_evaluated",
  "unsupported",
  "incomplete",
  "checks_passed",
  "approved",
  "issued",
] as const;

/** The headline for each state.
 *
 * Deliberately not a yes/no. "Not evaluated" is the state that used to read as success, and it has
 * to be as loud as a failure — a reader who sees a green tick over a regime with nothing loaded
 * has been told the opposite of the truth.
 */
/** The six states, in the words a reader needs (REP-02).
 *
 * The top three all mean the checks passed, and they are **different sentences** because they are
 * different facts: what EmitIQ checked, what a person signed, and what was filed. A screen that
 * said "passed" for all three would hide the two acts that have a human behind them.
 *
 * Each headline is named after what *was done*, never what it permits. "Every check has passed" is
 * not approval; "approved" is not a statement that the report is lawfully fit to file. The sentence
 * underneath carries the rest, including what was **not** checked — which is the part a reader
 * needs and a one-word grade can never carry.
 */
export const VERDICT_HEADLINE: Record<string, string> = {
  checks_passed: "Every check this regime defines has passed",
  approved: "Approved, and not yet issued",
  issued: "Issued: a filing exists for this obligation",
  incomplete: "This report is not ready as it stands",
  not_evaluated: "This report has not been checked",
  unsupported: "EmitIQ cannot produce what this regime requires",
};

/** A glyph per rung of the ladder, so the six states are legible at a glance and are never told
 * apart by colour alone (F-040).
 *
 * **Chosen for what was done, never for what it permits.** `checks_passed` is a checklist and not
 * a tick or a seal: a green tick there would make "every check has passed" read as approval, which
 * is the whole distinction the ladder exists to draw. A stamp is a person signing (`approved`); the
 * seal, filled because it is the terminal state, is a filing that exists (`issued`). Decorative:
 * the headline beside it names the state in words. */
export const VERDICT_ICON: Record<string, Icon> = {
  not_evaluated: CircleDashed,
  unsupported: Prohibit,
  incomplete: WarningCircle,
  checks_passed: ListChecks,
  approved: Stamp,
  issued: SealCheck,
};

/** The states in which the checks ran and passed.
 *
 * Mirrors `validation.PASSING` on the server, and it is a **set** for the same reason: a test of
 * `=== "checks_passed"` would paint an issued filing in the not-ready colour, which is the shape
 * of wrong that looks like a styling bug and reads as a warning.
 */
export const PASSING = new Set(["checks_passed", "approved", "issued"]);

/** What an **issued** filing's row says (walkthrough C2).
 *
 * A row in "Filings issued" exists because the filing was issued, so its headline is `issued`
 * whatever the six-state answer was a moment before the row was written. That recorded answer
 * (`approved` when an approval stood) used to be printed through `VERDICT_HEADLINE`, so a filing
 * issued minutes ago read "Approved, and not yet issued" beside its ledger number. The two facts
 * the old headline was standing in for - what the checks said, and whether an approval stood - are
 * separate fields now and are said as separate clauses.
 *
 * `approved_when_issued` is three-valued on purpose: `null` is a restatement, where the recorded
 * verdict is already `issued` and no longer separates approval from the earlier issue. Unknown is
 * not "no", and the sentence does not say no.
 */
export function issuedFiling(filing: {
  checks_verdict: string;
  approved_when_issued: boolean | null;
}): { headline: string; detail: string } {
  const checks: Record<string, string> = {
    checks_passed: "every check this regime defines had passed",
    incomplete: "checks had not passed when it was issued; the record lists them",
    not_evaluated: "its checks had not been run when it was issued",
    unsupported: "EmitIQ could not produce what the regime requires when it was issued",
  };
  const approval =
    filing.approved_when_issued === true
      ? "approved before issue"
      : filing.approved_when_issued === false
        ? "not approved before issue"
        : "a restatement: whether it was approved is not recorded separately";
  return {
    headline: VERDICT_HEADLINE["issued"] ?? "Issued",
    detail: `At issue: ${checks[filing.checks_verdict] ?? `its checks read "${filing.checks_verdict}"`} · ${approval}`,
  };
}

